import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Sparkles, AlertTriangle, Tag, Check } from 'lucide-react'
import Popover from '../components/Popover'
import Plot from 'react-plotly.js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  startSemanticAnalysis,
  startBERTopicAnalysis,
  getAnalysis,
  getTopicInsights,
  SemanticParams,
  SemanticResults,
  BERTopicParams,
  BERTopicResults,
  DocumentInfo,
} from '../api/analysis'
import { datasetApi } from '../api/search'
import { useSearchFilters } from '../contexts/SearchFiltersContext'
import AnalysisJobsPanel from '../components/AnalysisJobsPanel'
import AnalysisSourcePicker, { type AnalysisSource } from '../components/AnalysisSourcePicker'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
} from 'recharts'

const TOPIC_COLORS = [
  '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b',
]

// Helper: get consistent color for a topic ID (0-based or 1-based)
function getTopicColor(topicId: string | number): string {
  const id = typeof topicId === 'string' ? parseInt(topicId) : topicId
  return TOPIC_COLORS[Math.abs(id) % TOPIC_COLORS.length]
}

// Helper: display topic number (1-based for users)
function getTopicDisplay(topicId: string | number): string {
  const id = typeof topicId === 'string' ? parseInt(topicId) : topicId
  return String(id + 1)
}

type TopicTab = 'overview' | 'topics' | 'trends' | 'map'

function TabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: TopicTab; label: string }[]
  active: TopicTab
  onChange: (id: TopicTab) => void
}) {
  return (
    <div className="border-b border-slate-200 mb-4">
      <nav className="flex gap-1 -mb-px">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              active === t.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  )
}

function TopicsGridWithDocs({
  topicSummary,
  representativeDocs,
  topicCounts,
  filter,
  sort,
  topicNames = {},
  onEditTopic,
  editingTopic,
  editName,
  onEditNameChange,
  onSaveTopicName,
  title = 'Topics',
}: {
  topicSummary: Record<string, { term: string; score?: number; beta?: number }[]>
  representativeDocs?: Record<string, { doc_id: number; pmid: string; title: string | null; prob: number }[]>
  topicCounts?: Record<string, number>
  filter: string
  sort: 'number' | 'docs_desc'
  topicNames?: Record<string, string>
  onEditTopic?: (topic: string) => void
  editingTopic?: string | null
  editName?: string
  onEditNameChange?: (name: string) => void
  onSaveTopicName?: (topic: string) => void
  title?: string
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const allEntries = Object.entries(topicSummary)

  const f = filter.trim().toLowerCase()
  const filtered = !f
    ? allEntries
    : allEntries.filter(([topic, terms]) => {
        const label = `topic ${getTopicDisplay(topic)} ${topicNames[getTopicDisplay(topic)] || ''}`.toLowerCase()
        if (label.includes(f)) return true
        return terms.slice(0, 8).some(t => t.term.toLowerCase().includes(f))
      })

  const sorted = sort === 'docs_desc' && topicCounts
    ? [...filtered].sort((a, b) => (topicCounts[b[0]] || 0) - (topicCounts[a[0]] || 0))
    : [...filtered].sort((a, b) => parseInt(a[0]) - parseInt(b[0]))

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h3 className="text-lg font-semibold">{title} <span className="text-sm font-normal text-slate-500">({sorted.length} of {allEntries.length})</span></h3>
      </div>
      {sorted.length === 0 && (
        <div className="text-sm text-slate-500 mb-3">No topics match "{filter}".</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map(([topic, terms]) => {
          const isOpen = !!expanded[topic]
          const docs = representativeDocs?.[topic] || []
          const cnt = topicCounts?.[topic]
          return (
            <div key={topic} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div
                className="text-white px-3 py-2 font-bold flex items-center justify-between text-sm cursor-pointer"
                style={{ backgroundColor: getTopicColor(topic) }}
                onClick={() => setExpanded(prev => ({ ...prev, [topic]: !prev[topic] }))}
              >
                {editingTopic === topic ? (
                  <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                    <span className="whitespace-nowrap">T{getTopicDisplay(topic)}</span>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => onEditNameChange?.(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSaveTopicName?.(topic)
                      }}
                      className="flex-1 px-2 py-1 text-sm text-gray-800 rounded"
                      placeholder="Topic name…"
                      autoFocus
                    />
                    <button
                      onClick={() => onSaveTopicName?.(topic)}
                      className="text-xs bg-white text-gray-800 px-2 py-1 rounded hover:bg-gray-100"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5 flex-1 truncate">
                      <span className={`text-xs transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                      <span className="truncate">
                        {topicNames[getTopicDisplay(topic)]
                          ? `T${getTopicDisplay(topic)} · ${topicNames[getTopicDisplay(topic)]}`
                          : `Topic ${getTopicDisplay(topic)}`}
                      </span>
                    </span>
                    <span className="flex items-center gap-2 ml-2">
                      {cnt != null && (
                        <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">{cnt.toLocaleString()} docs</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onEditTopic?.(topic) }}
                        className="text-xs bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded"
                        title="Rename topic"
                      >
                        ✎
                      </button>
                    </span>
                  </>
                )}
              </div>

              <div className="p-3">
                <p className="text-sm text-gray-700">
                  {terms.slice(0, 8).map((t) => {
                    const score = t.score ?? t.beta ?? 0
                    return `${t.term} (${score.toFixed(3)})`
                  }).join(', ')}
                </p>

                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    {docs.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">No representative documents recorded for this topic.</p>
                    ) : (
                      <>
                        <div className="text-xs font-semibold text-slate-500 mb-1.5">Top documents</div>
                        <ul className="space-y-1.5">
                          {docs.slice(0, 10).map(d => (
                            <li key={d.doc_id} className="text-xs">
                              <a
                                href={`https://pubmed.ncbi.nlm.nih.gov/${d.pmid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-brand-600 hover:underline mr-1"
                              >
                                {d.pmid}
                              </a>
                              <span className="text-slate-700">{d.title || 'Untitled'}</span>
                              <span className="text-slate-400 ml-1">({Math.round((d.prob || 0) * 100)}%)</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TopicDistribution({ distribution }: { distribution: any[] }) {
  // Ensure consistent ordering and 1-based display
  const sortedDistribution = [...distribution].sort((a, b) => a.topic - b.topic)
  
  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <h3 className="text-lg font-semibold mb-4">Topic Distribution</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={sortedDistribution}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="topic" 
            tickFormatter={(value) => getTopicDisplay(value)}
            label={{ value: 'Topic', position: 'insideBottom', offset: -5 }} 
          />
          <YAxis label={{ value: 'Documents', angle: -90, position: 'insideLeft' }} />
          <Tooltip 
            formatter={(value: number, _name: string, props: any) => [
              `${value} docs`,
              `Topic ${getTopicDisplay(props.payload.topic)}`
            ]}
          />
          <Bar dataKey="n_docs" name="Documents">
            {sortedDistribution.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getTopicColor(entry.topic)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function TemporalChart({ documents }: { documents: DocumentInfo[] }) {
  const [showPercentage, setShowPercentage] = useState(false)
  
  const yearTopicCounts: Record<number, Record<number, number>> = {}
  
  documents.forEach(doc => {
    if (doc.year && doc.dominant_topic) {
      if (!yearTopicCounts[doc.year]) {
        yearTopicCounts[doc.year] = {}
      }
      if (!yearTopicCounts[doc.year][doc.dominant_topic]) {
        yearTopicCounts[doc.year][doc.dominant_topic] = 0
      }
      yearTopicCounts[doc.year][doc.dominant_topic]++
    }
  })

  const years = Object.keys(yearTopicCounts).map(Number).sort()
  const topics = Array.from(new Set(documents.map(d => d.dominant_topic))).filter(Boolean).sort()

  // Calculate totals per year for percentage mode
  const yearTotals: Record<number, number> = {}
  years.forEach(year => {
    yearTotals[year] = Object.values(yearTopicCounts[year]).reduce((a, b) => a + b, 0)
  })

  const data = years.map(year => {
    const row: any = { year }
    topics.forEach(topic => {
      const count = yearTopicCounts[year]?.[topic] || 0
      row[`topic_${topic}`] = showPercentage 
        ? yearTotals[year] > 0 ? ((count / yearTotals[year]) * 100).toFixed(1) : 0
        : count
    })
    return row
  })

  if (years.length < 2) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-4">Topic Evolution Over Time</h3>
        <p className="text-gray-500">Not enough temporal data (need 2+ years)</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Topic Evolution Over Time</h3>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={showPercentage}
            onChange={(e) => setShowPercentage(e.target.checked)}
            className="rounded"
          />
          Show percentages
        </label>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis label={showPercentage ? { value: '%', angle: -90, position: 'insideLeft' } : undefined} />
          <Tooltip formatter={(value: any, name: any) => [
            showPercentage ? `${value}%` : value,
            name.replace('Topic ', 'Topic ')
          ]} />
          {topics.map((topic) => (
            <Area
              key={topic}
              type="monotone"
              dataKey={`topic_${topic}`}
              name={`Topic ${getTopicDisplay(topic)}`}
              stackId="1"
              stroke={getTopicColor(topic)}
              fill={getTopicColor(topic)}
              fillOpacity={0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function BERTopicTemporalChart({ documents }: { documents: { year: number | null; topic: number }[] }) {
  const [showPercentage, setShowPercentage] = useState(false)
  
  const yearTopicCounts: Record<number, Record<number, number>> = {}
  
  documents.forEach(doc => {
    if (doc.year && doc.topic >= 0) {
      if (!yearTopicCounts[doc.year]) {
        yearTopicCounts[doc.year] = {}
      }
      if (!yearTopicCounts[doc.year][doc.topic]) {
        yearTopicCounts[doc.year][doc.topic] = 0
      }
      yearTopicCounts[doc.year][doc.topic]++
    }
  })

  const years = Object.keys(yearTopicCounts).map(Number).sort()
  const topics = Array.from(new Set(documents.map(d => d.topic))).filter(t => t >= 0).sort()

  // Calculate totals per year for percentage mode
  const yearTotals: Record<number, number> = {}
  years.forEach(year => {
    yearTotals[year] = Object.values(yearTopicCounts[year]).reduce((a, b) => a + b, 0)
  })

  const data = years.map(year => {
    const row: any = { year }
    topics.forEach(topic => {
      const count = yearTopicCounts[year]?.[topic] || 0
      row[`topic_${topic}`] = showPercentage 
        ? yearTotals[year] > 0 ? ((count / yearTotals[year]) * 100).toFixed(1) : 0
        : count
    })
    return row
  })

  if (years.length < 2) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-4">Topic Evolution Over Time</h3>
        <p className="text-gray-500">Not enough temporal data (need 2+ years)</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Topic Evolution Over Time</h3>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={showPercentage}
            onChange={(e) => setShowPercentage(e.target.checked)}
            className="rounded"
          />
          Show percentages
        </label>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis label={showPercentage ? { value: '%', angle: -90, position: 'insideLeft' } : undefined} />
          <Tooltip formatter={(value: any, name: any) => [
            showPercentage ? `${value}%` : value,
            name.replace('Topic ', 'Topic ')
          ]} />
          {topics.map((topic) => (
            <Area
              key={topic}
              type="monotone"
              dataKey={`topic_${topic}`}
              name={`Topic ${getTopicDisplay(topic)}`}
              stackId="1"
              stroke={getTopicColor(topic)}
              fill={getTopicColor(topic)}
              fillOpacity={0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function InteractiveDocumentMap({
  documents,
  method,
  isBertopic = false,
  topicNames = {}
}: {
  documents: any[]
  method: string
  isBertopic?: boolean
  topicNames?: Record<string, string>
}) {
  // For very large analyses (>3 k points) Plotly's first paint blocks
  // the main thread for several seconds even with scattergl — which
  // makes the whole page look frozen / white. Gate the render behind
  // an explicit button so the rest of the analysis (topic cards,
  // temporal chart, etc.) stays interactive.
  const HEAVY_THRESHOLD = 3000
  const [forceRender, setForceRender] = useState(documents.length <= HEAVY_THRESHOLD)
  const [showLabels, setShowLabels] = useState(false)
  const [showLegend, setShowLegend] = useState(true)

  if (!forceRender) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-2">Document Map ({method.toUpperCase()})</h3>
        <div className="text-sm text-gray-600 mb-3">
          This analysis has <strong>{documents.length.toLocaleString()}</strong> points.
          Rendering this many on one chart can take 5–15 seconds and briefly
          freeze the page. Click below when you're ready.
        </div>
        <button
          onClick={() => setForceRender(true)}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Render document map ({documents.length.toLocaleString()} points)
        </button>
      </div>
    )
  }
  const xKey = isBertopic ? 'umap_x' : (method === 'umap' ? 'umap_x' : 'lsa_x')
  const yKey = isBertopic ? 'umap_y' : (method === 'umap' ? 'umap_y' : 'lsa_y')
  const xLabel = method === 'umap' ? 'UMAP 1' : 'LSA Dim 1'
  const yLabel = method === 'umap' ? 'UMAP 2' : 'LSA Dim 2'
  const topicKey = isBertopic ? 'topic' : 'dominant_topic'

  const filteredDocs = documents
    .filter(d => d[xKey] !== undefined && d[yKey] !== undefined)
    .map(d => ({
      x: d[xKey],
      y: d[yKey],
      topic: d[topicKey],
      pmid: d.pmid,
      title: d.title || 'Unknown',
      journal: d.journal || '',
      year: d.year,
      prob: d.topic_prob || 0.5,
    }))

  if (filteredDocs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-4">Document Map ({method.toUpperCase()})</h3>
        <p className="text-gray-500">No coordinates available</p>
      </div>
    )
  }

  // Group by topic for Plotly traces
  const topics = Array.from(new Set(filteredDocs.map(d => d.topic))).filter(t => t >= 0).sort()
  
  // For large analyses (>5k points), switch to WebGL-accelerated scattergl —
  // SVG `scatter` chokes the browser at that scale and can crash the tab.
  // scattergl also doesn't draw per-point marker borders, so we omit `line`
  // when GL is active.
  const useGL = filteredDocs.length > 5000
  const traces = topics.map(topic => {
    const topicDocs = filteredDocs.filter(d => d.topic === topic)
    const baseMarker: any = {
      size: topicDocs.map(d => 6 + (d.prob || 0.5) * 8),
      color: getTopicColor(topic),
      opacity: 0.7,
    }
    if (!useGL) {
      baseMarker.line = { color: 'white', width: 1 }
    }
    return {
      x: topicDocs.map(d => d.x),
      y: topicDocs.map(d => d.y),
      mode: 'markers' as const,
      type: (useGL ? 'scattergl' : 'scatter') as any,
      name: `Topic ${getTopicDisplay(topic)}`,
      marker: baseMarker,
      text: topicDocs.map(d =>
        `<b>${d.title}</b><br>` +
        `${d.journal} ${d.year ? `(${d.year})` : ''}<br>` +
        `PMID: ${d.pmid}<br>` +
        `Topic ${getTopicDisplay(d.topic)} (${Math.round((d.prob || 0) * 100)}%)`
      ),
      hoverinfo: 'text' as const,
    }
  })

  // Compute topic centroids for labels
  const annotations = showLabels ? topics.map(topic => {
    const topicDocs = filteredDocs.filter(d => d.topic === topic)
    const centroidX = topicDocs.reduce((sum, d) => sum + d.x, 0) / topicDocs.length
    const centroidY = topicDocs.reduce((sum, d) => sum + d.y, 0) / topicDocs.length
    const displayNum = getTopicDisplay(topic)
    const label = topicNames[displayNum] 
      ? `${displayNum}. ${topicNames[displayNum]}` 
      : `T${displayNum}`
    
    return {
      x: centroidX,
      y: centroidY,
      text: label,
      showarrow: false,
      font: {
        size: 10,
        color: getTopicColor(topic),
        weight: 700,
      },
      bgcolor: 'rgba(255,255,255,0.85)',
      bordercolor: getTopicColor(topic),
      borderwidth: 1,
      borderpad: 2,
      opacity: 0.9,
    }
  }) : []

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Document Map ({method.toUpperCase()})</h3>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            Show topic labels
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={showLegend}
              onChange={(e) => setShowLegend(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            Show legend
          </label>
        </div>
      </div>
      <div className="text-xs text-gray-500 mb-2">
        Scroll to zoom • Drag to pan • Box select (hold shift) • Double-click to reset
      </div>
      <Plot
        data={traces}
        layout={{
          autosize: true,
          height: 500,
          margin: { l: 50, r: 20, t: 10, b: 50 },
          xaxis: { 
            title: xLabel,
            zeroline: false,
            gridcolor: '#f0f0f0',
          },
          yaxis: { 
            title: yLabel,
            zeroline: false,
            gridcolor: '#f0f0f0',
          },
          hovermode: 'closest' as const,
          showlegend: showLegend,
          legend: {
            x: 1,
            y: 1,
            xanchor: 'right' as const,
            yanchor: 'top' as const,
          },
          paper_bgcolor: 'white',
          plot_bgcolor: 'white',
          annotations,
        }}
        config={{
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d'],
          toImageButtonOptions: {
            format: 'png',
            filename: 'umap_document_map',
            height: 600,
            width: 800,
            scale: 2,
          },
        }}
        style={{ width: '100%', height: '500px' }}
      />
    </div>
  )
}

function TopicHeatmap({ topicTopTerms }: { topicTopTerms: any[] }) {
  const topics = Array.from(new Set(topicTopTerms.map(t => t.topic))).sort((a, b) => a - b)
  const termsByTopic: Record<number, any[]> = {}
  
  topics.forEach(topic => {
    termsByTopic[topic] = topicTopTerms
      .filter(t => t.topic === topic)
      .sort((a, b) => (b.beta || b.weight) - (a.beta || a.weight))
      .slice(0, 10)
  })

  const allTerms = Array.from(new Set(topicTopTerms.map(t => t.term)))
  const maxBeta = Math.max(...topicTopTerms.map(t => t.beta || t.weight))

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 overflow-x-auto">
      <h3 className="text-lg font-semibold mb-4">Topic-Term Heatmap</h3>
      <div className="min-w-max">
        <div className="grid" style={{ gridTemplateColumns: `100px repeat(${topics.length}, 80px)` }}>
          <div className="font-semibold text-sm p-2">Term</div>
          {topics.map(topic => (
            <div key={topic} className="font-semibold text-sm p-2 text-center">
              Topic {getTopicDisplay(topic)}
            </div>
          ))}
          
          {allTerms.slice(0, 30).map(term => (
            <>
              <div key={`label-${term}`} className="text-xs p-2 border-t">{term}</div>
              {topics.map(topic => {
                const termData = termsByTopic[topic]?.find((t: any) => t.term === term)
                const beta = termData?.beta || termData?.weight || 0
                const intensity = beta / maxBeta
                return (
                  <div 
                    key={`${topic}-${term}`}
                    className="p-2 border-t text-center text-xs"
                    style={{ 
                      backgroundColor: `rgba(52, 152, 219, ${intensity})`,
                      color: intensity > 0.5 ? 'white' : 'black'
                    }}
                  >
                    {beta > 0 ? beta.toFixed(3) : ''}
                  </div>
                )
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  )
}

function NgramChart({ documents, textSource }: { documents: DocumentInfo[], textSource: string }) {
  const bigrams: Record<string, number> = {}
  
  documents.forEach(doc => {
    const text = (textSource === 'title' ? doc.title : `${doc.title} ${(doc as any).text || ''}`) || ''
    const words = text.toLowerCase().split(/\W+/).filter(w => w.length > 2 && !['the','and','for','are','but','not','you','all','can','had','her','was','one','our','out','day','get','has','him','his','how','its','may','new','now','old','see','two','who','boy','did','she','use','her','way','many','oil','sit','set','run','eat','far','sea','eye','ago','off','too','any','say','man','try','ask','end','why','let','put','say','she','try','way','own','say','too','old','tell','very','when','much','would','there','their','what','said','each','which','will','about','could','other','after','first','never','these','think','where','being','every','great','might','shall','still','those','while','this','that','from','they','know','want','been','good','much','some','time','than','them','well','were'].includes(w))
    
    for (let i = 0; i < words.length - 1; i++) {
      const bigram = `${words[i]} ${words[i + 1]}`
      bigrams[bigram] = (bigrams[bigram] || 0) + 1
    }
  })

  const sortedBigrams = Object.entries(bigrams)
    .filter(([_, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)

  if (sortedBigrams.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-semibold mb-4">N-gram Analysis</h3>
        <p className="text-gray-500">Not enough data for n-gram analysis</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <h3 className="text-lg font-semibold mb-4">N-gram Analysis (Bigrams)</h3>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={sortedBigrams.map(([ngram, count]) => ({ ngram, count }))} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="ngram" type="category" width={150} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" fill="#8e44ad" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function LSATermLoadings({ lsa }: { lsa: any }) {
  if (!lsa || !lsa.v) return null

  const dimensions = Math.min(2, lsa.v.length)
  const data = []
  
  for (let dim = 0; dim < dimensions; dim++) {
    const loadings = lsa.v[dim]
      .map((val: number, idx: number) => ({ term: lsa.terms[idx], loading: val, dim: dim + 1 }))
      .sort((a: any, b: any) => Math.abs(b.loading) - Math.abs(a.loading))
      .slice(0, 10)
    
    data.push(...loadings)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <h3 className="text-lg font-semibold mb-4">LSA Term Loadings</h3>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data.filter((d: any) => d.dim === 1)} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="term" type="category" width={120} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="loading" fill="#3498db" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function AnalysisPage() {
  const [searchParams] = useSearchParams()
  const searchId = searchParams.get('search_id')
  const queryClient = useQueryClient()
  const { datasetAnalysis, datasetTopicNames, setCurrentAnalysis, setCurrentTopicNames, setEnhancedTopicAnnotations, loadedDatasetId, filteredPmids } = useSearchFilters()
  const [source, setSource] = useState<AnalysisSource>(
    { type: 'search', searchId: searchId || null }
  )
  
  const [analysisMode, setAnalysisMode] = useState<'semantic' | 'bertopic'>('semantic')
  
  const [semanticConfig, setSemanticConfig] = useState<SemanticParams>({
    search_id: searchId || '',
    text_source: 'abstract',
    n_topics: 5,
    n_terms: 10,
    doc_map_method: 'umap',
    umap_neighbors: 15,
    umap_min_dist: 0.1,
    lsa_dimensions: 10,
  })
  
  const [bertopicConfig, setBertopicConfig] = useState<BERTopicParams>({
    search_id: searchId || '',
    text_source: 'abstract',
    n_topics: undefined,
    min_topic_size: 5,
    embedding_model: 'all-MiniLM-L6-v2',
  })
  
  const [activeTab, setActiveTab] = useState<'lda' | 'nmf'>('lda')
  // High-level view tabs across both BERTopic and semantic results.
  type TopicViewTab = 'overview' | 'topics' | 'trends' | 'map'
  const [topicViewTab, setTopicViewTab] = useState<TopicViewTab>('overview')
  const [topicFilter, setTopicFilter] = useState('')
  const [topicSort, setTopicSort] = useState<'number' | 'docs_desc'>('number')
  const [status, setStatus] = useState<string>('idle')
  const [results, setResults] = useState<SemanticResults | BERTopicResults | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null)
  
  const [insights, setInsights] = useState<string | null>(null)
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [insightsError, setInsightsError] = useState<string | null>(null)
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const [limitMessage, setLimitMessage] = useState('')
  const [topicNames, setTopicNames] = useState<Record<string, string>>({})
  const [editingTopic, setEditingTopic] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [generateInsight, setGenerateInsight] = useState(true)
  const [generateTopicNames, setGenerateTopicNames] = useState(true)
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string | null>(null)

  const startAnalysis = async () => {
    const config = analysisMode === 'semantic' ? semanticConfig : bertopicConfig

    // Semantic analysis is restricted to saved sources (dataset or bookmark folder).
    if (analysisMode === 'semantic' && source.type === 'search') {
      setError('Semantic analysis requires a saved dataset or bookmark folder. Save your search as a dataset first.')
      return
    }

    if (!config.search_id && source.type === 'search') {
      setError('No search ID provided')
      return
    }

    setStatus('running')
    setError(null)
    setResults(null)

    try {
      // Layer the chosen source onto the analysis config so the backend
      // runner picks up dataset_id / bookmark_folder_id / explicit pmids.
      const sourceParams: any = {}
      if (source.type === 'search') {
        if (loadedDatasetId) sourceParams.dataset_id = loadedDatasetId
        if (filteredPmids.size > 0) sourceParams.pmids = Array.from(filteredPmids)
      } else if (source.type === 'dataset') {
        sourceParams.dataset_id = source.datasetId
        sourceParams.search_id = undefined
      } else if (source.type === 'bookmark_folder') {
        sourceParams.bookmark_folder_id = source.folderId
        sourceParams.search_id = undefined
      }

      const aiFlags = {
        generate_insight: generateInsight,
        generate_topic_names: generateTopicNames,
      }

      let response
      if (analysisMode === 'semantic') {
        response = await startSemanticAnalysis(
          `Semantic Analysis - ${semanticConfig.text_source}`,
          { ...semanticConfig, ...sourceParams, ...aiFlags } as any,
        )
      } else {
        response = await startBERTopicAnalysis(
          `BERTopic Analysis - ${bertopicConfig.text_source}`,
          { ...bertopicConfig, ...sourceParams, ...aiFlags } as any,
        )
      }
      queryClient.invalidateQueries({ queryKey: ['analyses-list', 'topic'] })

      setCurrentAnalysisId(response.analysis_id)
      const interval = setInterval(() => {
        pollAnalysis(response.analysis_id)
      }, 2000)
      setPollingInterval(interval)
    } catch (err: any) {
      setStatus('error')
      setError(err.response?.data?.detail || err.message)
    }
  }

  // Legacy analyses ran with a 0-based prompt; the plot uses 1-based topic
  // labels. If a saved names dict has "0" as a key, treat it as 0-based and
  // shift every key up by 1 so it matches the plot.
  const normalizeTopicNames = (names: Record<string, string> | null | undefined): Record<string, string> => {
    if (!names) return {}
    if (!('0' in names)) return names
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(names)) {
      const n = parseInt(k, 10)
      out[Number.isNaN(n) ? k : String(n + 1)] = v
    }
    return out
  }

  const loadFromJob = async (id: string) => {
    setStatus('running')
    setError(null)
    setResults(null)
    setCurrentAnalysisId(id)
    try {
      const analysis = await getAnalysis(id)
      if (analysis.status === 'complete' && analysis.results) {
        setResults(analysis.results)
        setCurrentAnalysis(analysis.results)
        // Hydrate auto-generated insight + topic names if the job saved them
        const savedInsight = (analysis.results as any)?.ai_insight
        const savedNames = normalizeTopicNames((analysis.results as any)?.ai_topic_names)
        if (savedInsight) setInsights(savedInsight)
        setTopicNames(Object.keys(savedNames).length > 0 ? savedNames : {})
        setCurrentTopicNames(savedNames)
        setEnhancedTopicAnnotations(true)
        setStatus('complete')
        if (searchId) queryClient.invalidateQueries({ queryKey: ['search', searchId] })
      } else if (analysis.status === 'error') {
        setError(analysis.error_message || 'Analysis failed')
        setStatus('error')
      } else if (analysis.status === 'cancelled') {
        setError('Analysis was cancelled')
        setStatus('error')
      } else {
        // Still running — start polling
        const interval = setInterval(() => pollAnalysis(id), 2000)
        setPollingInterval(interval)
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load')
      setStatus('error')
    }
  }

  const pollAnalysis = async (id: string) => {
    try {
      const analysis = await getAnalysis(id)
      setStatus(analysis.status)
      
      if (analysis.status === 'complete' && analysis.results) {
        setResults(analysis.results)
        setCurrentAnalysis(analysis.results)
        const savedInsight = (analysis.results as any)?.ai_insight
        const savedNames = normalizeTopicNames((analysis.results as any)?.ai_topic_names)
        if (savedInsight) setInsights(savedInsight)
        if (Object.keys(savedNames).length > 0) setTopicNames(savedNames)
        setCurrentTopicNames(savedNames)
        setEnhancedTopicAnnotations(true)
        if (searchId) {
          queryClient.invalidateQueries({ queryKey: ['search', searchId] })
        }
        if (pollingInterval) {
          clearInterval(pollingInterval)
          setPollingInterval(null)
        }
      } else if (analysis.status === 'error') {
        setError(analysis.error_message || 'Analysis failed')
        if (pollingInterval) {
          clearInterval(pollingInterval)
          setPollingInterval(null)
        }
      }
    } catch (err: any) {
      console.error('Polling error:', err)
    }
  }

  const fetchInsights = async () => {
    if (!results) return
    
    setInsightsLoading(true)
    setInsightsError(null)
    setInsights(null)
    
    try {
      const rawTopicSummary = isSemanticResult(results) 
        ? (activeTab === 'lda' ? results.topic_summary : (results.nmf ? results.nmf.top_terms.reduce((acc: any, t: any) => {
            if (!acc[t.topic]) acc[t.topic] = []
            acc[t.topic].push(t)
            return acc
          }, {}) : results.topic_summary))
        : results.topic_summary
      
      // Remap topic keys to 1-based numbering so AI insights match the UI display
      const topicSummary: Record<string, any[]> = {}
      Object.entries(rawTopicSummary).forEach(([key, terms]: [string, any]) => {
        const oneBasedKey = String(parseInt(key) + 1)
        topicSummary[oneBasedKey] = terms
      })
      
      const response = await getTopicInsights(
        topicSummary,
        isSemanticResult(results) ? 'semantic' : 'bertopic',
        results.n_documents
      )
      
      setInsights(response.insights)
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to get insights'
      if (err.response?.status === 429) {
        setLimitMessage(msg)
        setLimitModalOpen(true)
      } else {
        setInsightsError(msg)
      }
    } finally {
      setInsightsLoading(false)
    }
  }

  const parseTopicNamesFromInsights = (insightsText: string): Record<string, string> => {
    const names: Record<string, string> = {}
    // Match patterns like "## Topic 1: Exosome-Based Drug Delivery"
    // Be strict: only capture up to newline, period+space, or 60 chars
    const regex = /(?:##?\s*)?[Tt]opic\s+(\d+)[:\-\s]+(.{2,60}?)(?=\n|\.\s|\.$|$)/g
    let match
    while ((match = regex.exec(insightsText)) !== null) {
      const topicNum = match[1]
      let name = match[2].trim()
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/^["']|["']$/g, '')
        .trim()
      
      // Skip if it looks like an explanation (contains "explains", "describes", "focuses on")
      const explanationWords = ['explains', 'describes', 'focuses', 'represents', 'covers', 'deals with', 'is about', 'refers to']
      if (explanationWords.some(w => name.toLowerCase().includes(w))) {
        continue
      }
      
      // Limit to reasonable title length
      if (name.length > 2 && name.length <= 60) {
        names[topicNum] = name
      }
    }
    return names
  }

  const applyAITitles = () => {
    if (!insights) return
    const parsed = parseTopicNamesFromInsights(insights)
    console.log('DEBUG applyAITitles:', {
      insightsLength: insights?.length,
      parsedKeys: Object.keys(parsed),
      parsed: parsed,
      currentTopicNamesKeys: Object.keys(topicNames)
    })
    if (Object.keys(parsed).length > 0) {
      const merged = { ...topicNames, ...parsed }
      console.log('DEBUG setting topic names:', merged)
      setTopicNames(merged)
      setCurrentTopicNames(merged)
    }
  }

  const handleEditTopic = (topicNum: string) => {
    setEditingTopic(topicNum)
    setEditName(topicNames[getTopicDisplay(topicNum)] || '')
  }

  const handleSaveTopicName = (topicNum: string) => {
    if (editName.trim()) {
      const updated = { ...topicNames, [getTopicDisplay(topicNum)]: editName.trim() }
      setTopicNames(updated)
      setCurrentTopicNames(updated)
    }
    setEditingTopic(null)
    setEditName('')
  }

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [pollingInterval])

  useEffect(() => {
    if (searchId) {
      setSemanticConfig(prev => ({ ...prev, search_id: searchId }))
      setBertopicConfig(prev => ({ ...prev, search_id: searchId }))
    }
  }, [searchId])

  // Restore analysis from loaded dataset context
  useEffect(() => {
    if (datasetAnalysis && !results) {
      setResults(datasetAnalysis)
      setStatus('complete')
      if (datasetAnalysis.topic_summary && !datasetAnalysis.representative_docs) {
        setAnalysisMode('semantic')
      } else if (datasetAnalysis.representative_docs) {
        setAnalysisMode('bertopic')
      }
    }
    if (datasetTopicNames && Object.keys(datasetTopicNames).length > 0) {
      setTopicNames(datasetTopicNames)
    }
  }, [datasetAnalysis, datasetTopicNames])

  // Sync current topic names to context for search results display
  useEffect(() => {
    if (Object.keys(topicNames).length > 0) {
      setCurrentTopicNames(topicNames)
    }
  }, [topicNames])

  const isBertopicResult = (r: any): r is BERTopicResults => r && 'representative_docs' in r

  function InsightsPanel() {
    if (!results) return null
    
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            AI Topic Insights
          </h3>
          <button
            onClick={fetchInsights}
            disabled={insightsLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center gap-2"
          >
            {insightsLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                {insights ? 'Regenerate Insights' : 'Get AI Insights'}
              </>
            )}
          </button>
        </div>
        
        {insightsError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm mb-4">
            {insightsError}
          </div>
        )}
        
        {insights && (
          <div className="space-y-4">
            <button
              onClick={applyAITitles}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Apply AI Titles to Topics
            </button>
            <div className="prose prose-sm max-w-none">
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <h4 className="text-sm font-semibold text-purple-900 mb-2">AI-Generated Topic Interpretation</h4>
                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                  {insights}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {!insights && !insightsError && !insightsLoading && (
          <p className="text-sm text-gray-500">
            Click "Get AI Insights" to generate AI-powered topic names and explanations based on the discovered topics.
          </p>
        )}
      </div>
    )
  }
  const isSemanticResult = (r: any): r is SemanticResults => r && 'topic_top_terms' in r

  const { data: myDatasets } = useQuery({
    queryKey: ['my-datasets'],
    queryFn: () => datasetApi.listMyDatasets(),
    enabled: !!searchId,
  })

  const annotateMutation = useMutation({
    mutationFn: ({ datasetId, analysisId }: { datasetId: string; analysisId: string }) =>
      datasetApi.annotateWithAnalysis(datasetId, analysisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
      queryClient.invalidateQueries({ queryKey: ['my-datasets-for-source-picker'] })
    },
  })

  function AnnotateDatasetPanel() {
    if (!results || status !== 'complete' || !currentAnalysisId) return null
    if (source.type !== 'dataset') return null
    const targetDataset = myDatasets?.find((d: any) => d.dataset_id === source.datasetId)
    if (!targetDataset) return null

    const isAlreadyAnnotatedByThis = targetDataset.annotation_analysis_id === currentAnalysisId
    const annotatedByOther = !!targetDataset.annotation_analysis_id && !isAlreadyAnnotatedByThis
    const otherName = targetDataset.annotation_analysis_name || 'another analysis'
    const otherType = targetDataset.analysis_type ? ` (${targetDataset.analysis_type})` : ''

    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Annotate dataset with this analysis
        </h3>
        <p className="text-xs text-gray-600 mb-3">
          Tags each article in "<span className="font-medium">{source.datasetName || 'this dataset'}</span>" with the topic it was
          assigned to by this analysis. You can re-annotate at any time by loading a different analysis and clicking again.
        </p>
        {isAlreadyAnnotatedByThis ? (
          <div className="flex items-center gap-2 text-sm text-green-700">
            <Check className="w-4 h-4" />
            This analysis already annotates the dataset.
            <button
              onClick={() => {
                if (confirm('Remove the topic annotation from this dataset? The analysis itself stays.')) {
                  datasetApi.clearAnnotation(source.datasetId).then(() => {
                    queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
                    queryClient.invalidateQueries({ queryKey: ['my-datasets-for-source-picker'] })
                  })
                }
              }}
              className="ml-3 text-xs text-red-600 hover:text-red-800 underline"
            >
              Clear annotation
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {annotatedByOther && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                This dataset is currently annotated by <span className="font-medium">{otherName}</span>{otherType}. Clicking the
                button below will replace that annotation with this one.
              </div>
            )}
            <button
              onClick={() => annotateMutation.mutate({ datasetId: source.datasetId, analysisId: currentAnalysisId })}
              disabled={annotateMutation.isPending}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Tag className="w-4 h-4" />
              {annotateMutation.isPending
                ? 'Annotating…'
                : annotatedByOther
                  ? 'Switch annotation to this analysis'
                  : 'Annotate this dataset'}
            </button>
            {annotateMutation.isError && (
              <p className="text-xs text-red-600">
                {(annotateMutation.error as any)?.response?.data?.detail || 'Failed to annotate.'}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <AnalysisJobsPanel type="topic" onLoad={loadFromJob} title="Topic analyses" />

      <AnalysisSourcePicker
        value={source}
        onChange={setSource}
        searchId={searchId}
        currentFilteredPmids={filteredPmids.size > 0 ? Array.from(filteredPmids) : undefined}
        allowedSources={analysisMode === 'semantic' ? ['dataset', 'bookmark_folder'] : undefined}
      />

      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-2xl font-bold mb-4">Topic Analysis</h2>
        <p className="text-gray-600 mb-6">
          Extract topics and term relationships from abstracts using traditional methods (LDA/NMF/LSA/UMAP)
          or modern transformer-based BERTopic.
        </p>

        {/* Analysis mode selector */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setAnalysisMode('semantic')}
            className={`px-4 py-2 rounded-md font-medium ${
              analysisMode === 'semantic'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Semantic (LDA/NMF/LSA/UMAP)
          </button>
          <button
            onClick={() => setAnalysisMode('bertopic')}
            className={`px-4 py-2 rounded-md font-medium ${
              analysisMode === 'bertopic'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            BERTopic (Transformers)
          </button>
        </div>

        {analysisMode === 'semantic' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">Text Source</label>
              <select
                value={semanticConfig.text_source}
                onChange={(e) => setSemanticConfig({ ...semanticConfig, text_source: e.target.value as any })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="abstract">Abstract</option>
                <option value="title">Title</option>
                <option value="mesh">MeSH Terms</option>
                <option value="keywords">Keywords</option>
                <option value="both">Title + Abstract</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Number of Topics</label>
              <input
                type="number"
                min={2}
                max={20}
                value={semanticConfig.n_topics}
                onChange={(e) => setSemanticConfig({ ...semanticConfig, n_topics: parseInt(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Terms per Topic</label>
              <input
                type="number"
                min={5}
                max={30}
                value={semanticConfig.n_terms}
                onChange={(e) => setSemanticConfig({ ...semanticConfig, n_terms: parseInt(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Document Map Method</label>
              <select
                value={semanticConfig.doc_map_method}
                onChange={(e) => setSemanticConfig({ ...semanticConfig, doc_map_method: e.target.value as any })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="umap">UMAP</option>
                <option value="lsa">LSA (SVD)</option>
              </select>
            </div>

            {semanticConfig.doc_map_method === 'umap' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">UMAP Neighbors</label>
                  <input
                    type="number"
                    min={2}
                    max={50}
                    value={semanticConfig.umap_neighbors}
                    onChange={(e) => setSemanticConfig({ ...semanticConfig, umap_neighbors: parseInt(e.target.value) })}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">UMAP Min Distance</label>
                  <input
                    type="number"
                    min={0.01}
                    max={0.99}
                    step={0.01}
                    value={semanticConfig.umap_min_dist}
                    onChange={(e) => setSemanticConfig({ ...semanticConfig, umap_min_dist: parseFloat(e.target.value) })}
                    className="w-full border rounded-md px-3 py-2"
                  />
                </div>
              </>
            )}

            {semanticConfig.doc_map_method === 'lsa' && (
              <div>
                <label className="block text-sm font-medium mb-1">LSA Dimensions</label>
                <input
                  type="number"
                  min={2}
                  max={50}
                  value={semanticConfig.lsa_dimensions}
                  onChange={(e) => setSemanticConfig({ ...semanticConfig, lsa_dimensions: parseInt(e.target.value) })}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">Text Source</label>
              <select
                value={bertopicConfig.text_source}
                onChange={(e) => setBertopicConfig({ ...bertopicConfig, text_source: e.target.value as any })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="abstract">Abstract</option>
                <option value="title">Title</option>
                <option value="mesh">MeSH Terms</option>
                <option value="keywords">Keywords</option>
                <option value="both">Title + Abstract</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Number of Topics</label>
              <input
                type="number"
                min={2}
                max={50}
                placeholder="Auto (leave empty)"
                value={bertopicConfig.n_topics || ''}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : undefined
                  setBertopicConfig({ ...bertopicConfig, n_topics: val })
                }}
                className="w-full border rounded-md px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for automatic topic detection</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Min Topic Size</label>
              <input
                type="number"
                min={2}
                max={100}
                value={bertopicConfig.min_topic_size}
                onChange={(e) => setBertopicConfig({ ...bertopicConfig, min_topic_size: parseInt(e.target.value) })}
                className="w-full border rounded-md px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum documents per topic</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Embedding Model</label>
              <select
                value={bertopicConfig.embedding_model}
                onChange={(e) => setBertopicConfig({ ...bertopicConfig, embedding_model: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="all-MiniLM-L6-v2">all-MiniLM-L6-v2 (fast)</option>
                <option value="all-mpnet-base-v2">all-mpnet-base-v2 (accurate)</option>
              </select>
            </div>
          </div>
        )}

        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-4 mt-2">
          <div className="text-xs font-medium text-purple-900 mb-2 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> AI add-ons (run with the analysis)
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={generateInsight}
              onChange={(e) => setGenerateInsight(e.target.checked)}
              className="accent-purple-600"
            />
            Generate AI insight (paragraph summary across topics)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer mt-1">
            <input
              type="checkbox"
              checked={generateTopicNames}
              onChange={(e) => setGenerateTopicNames(e.target.checked)}
              className="accent-purple-600"
            />
            Generate AI topic names (short label per topic)
          </label>
          <p className="text-xs text-slate-500 mt-1">Both run automatically after the analysis completes and are saved with the job. Uncheck to skip and save AI units.</p>
        </div>

        <button
          onClick={startAnalysis}
          disabled={status === 'running'}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'running' ? 'Running Analysis...' : 'Run Analysis'}
        </button>

        {status === 'running' && (
          <div className="mt-4 flex items-center text-blue-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
            Processing {analysisMode === 'semantic' ? 'semantic' : 'BERTopic'} analysis... This may take a minute.
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            Error: {error}
          </div>
        )}
      </div>

      {/* Results */}
      {results && isSemanticResult(results) && (() => {
        const semanticCounts: Record<string, number> = {}
        for (const d of results.topic_distribution || []) {
          semanticCounts[String(d.topic)] = d.n_docs
        }
        // NMF top_terms is array<{topic, term, beta?, ...}>; group by topic.
        const nmfSummary = results.nmf
          ? results.nmf.top_terms.reduce((acc: any, t: any) => {
              if (!acc[t.topic]) acc[t.topic] = []
              acc[t.topic].push(t)
              return acc
            }, {})
          : null
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-lg font-semibold mb-2">Analysis Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Documents</p>
                  <p className="text-2xl font-bold">{results.n_documents}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Vocabulary</p>
                  <p className="text-2xl font-bold">{results.vocabulary_size}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Topics</p>
                  <p className="text-2xl font-bold">{results.config.n_topics}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Method</p>
                  <p className="text-2xl font-bold uppercase">{results.config.doc_map_method}</p>
                </div>
              </div>
            </div>

            <TabBar
              tabs={[
                { id: 'overview', label: 'Overview' },
                { id: 'topics', label: `Topics (${Object.keys(results.topic_summary).length})` },
                { id: 'trends', label: 'Trends' },
                { id: 'map', label: 'Document map' },
              ]}
              active={topicViewTab}
              onChange={setTopicViewTab}
            />

            {topicViewTab === 'overview' && (
              <>
                <InsightsPanel />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <TopicHeatmap topicTopTerms={results.topic_top_terms} />
                  <TopicDistribution distribution={results.topic_distribution} />
                </div>
                {results.nmf && (
                  <div className="bg-white rounded-lg shadow-sm border p-4">
                    <h3 className="text-lg font-semibold mb-2">NMF Reconstruction Error</h3>
                    <p className="text-2xl font-bold">{results.nmf.reconstruction_err.toFixed(4)}</p>
                    <p className="text-sm text-gray-600">Iterations: {results.nmf.n_iter}</p>
                  </div>
                )}
              </>
            )}

            {topicViewTab === 'topics' && (
              <div className="space-y-3">
                {results.nmf && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActiveTab('lda')}
                      className={`px-3 py-1.5 text-sm rounded ${activeTab === 'lda' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      LDA
                    </button>
                    <button
                      onClick={() => setActiveTab('nmf')}
                      className={`px-3 py-1.5 text-sm rounded ${activeTab === 'nmf' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      NMF
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={topicFilter}
                    onChange={(e) => setTopicFilter(e.target.value)}
                    placeholder="Filter by term or name…"
                    className="flex-1 min-w-[14rem] px-3 py-2 border border-slate-300 rounded text-sm"
                  />
                  <select
                    value={topicSort}
                    onChange={(e) => setTopicSort(e.target.value as any)}
                    className="px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="number">Sort by topic number</option>
                    <option value="docs_desc">Sort by document count (desc)</option>
                  </select>
                </div>
                <TopicsGridWithDocs
                  topicSummary={activeTab === 'nmf' && nmfSummary ? nmfSummary : (results.topic_summary as any)}
                  representativeDocs={(results as any).representative_docs}
                  topicCounts={semanticCounts}
                  filter={topicFilter}
                  sort={topicSort}
                  title={activeTab === 'nmf' ? 'NMF topics' : 'LDA topics'}
                  topicNames={topicNames}
                  onEditTopic={handleEditTopic}
                  editingTopic={editingTopic}
                  editName={editName}
                  onEditNameChange={setEditName}
                  onSaveTopicName={handleSaveTopicName}
                />
              </div>
            )}

            {topicViewTab === 'trends' && (
              <div className="space-y-4">
                <TemporalChart documents={results.documents} />
                <NgramChart documents={results.documents} textSource={results.config.text_source} />
                {results.config.doc_map_method === 'lsa' && results.lsa && (
                  <LSATermLoadings lsa={results.lsa} />
                )}
              </div>
            )}

            {topicViewTab === 'map' && (
              <InteractiveDocumentMap
                documents={results.documents}
                method={results.config.doc_map_method}
                topicNames={topicNames}
              />
            )}
          </div>
        )
      })()}

      {results && isBertopicResult(results) && (() => {
        const bertopicCounts: Record<string, number> = {}
        for (const d of results.topic_distribution || []) {
          bertopicCounts[String(d.topic)] = d.n_docs
        }
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-lg font-semibold mb-2">BERTopic Analysis Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Documents</p>
                  <p className="text-2xl font-bold">{results.n_documents}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Topics Found</p>
                  <p className="text-2xl font-bold">{results.n_topics}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Model</p>
                  <p className="text-sm font-bold">{results.model_info.embedding_model}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Probabilities</p>
                  <p className="text-2xl font-bold">{results.model_info.has_probabilities ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>

            <TabBar
              tabs={[
                { id: 'overview', label: 'Overview' },
                { id: 'topics', label: `Topics (${Object.keys(results.topic_summary).length})` },
                { id: 'trends', label: 'Trends' },
                { id: 'map', label: 'Document map' },
              ]}
              active={topicViewTab}
              onChange={setTopicViewTab}
            />

            {topicViewTab === 'overview' && (
              <>
                <InsightsPanel />
                <TopicDistribution distribution={results.topic_distribution} />
              </>
            )}

            {topicViewTab === 'topics' && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={topicFilter}
                    onChange={(e) => setTopicFilter(e.target.value)}
                    placeholder="Filter by term or name…"
                    className="flex-1 min-w-[14rem] px-3 py-2 border border-slate-300 rounded text-sm"
                  />
                  <select
                    value={topicSort}
                    onChange={(e) => setTopicSort(e.target.value as any)}
                    className="px-3 py-2 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="number">Sort by topic number</option>
                    <option value="docs_desc">Sort by document count (desc)</option>
                  </select>
                </div>
                <TopicsGridWithDocs
                  topicSummary={results.topic_summary}
                  representativeDocs={results.representative_docs}
                  topicCounts={bertopicCounts}
                  filter={topicFilter}
                  sort={topicSort}
                  title="BERTopic topics"
                  topicNames={topicNames}
                  onEditTopic={handleEditTopic}
                  editingTopic={editingTopic}
                  editName={editName}
                  onEditNameChange={setEditName}
                  onSaveTopicName={handleSaveTopicName}
                />
              </div>
            )}

            {topicViewTab === 'trends' && (
              <BERTopicTemporalChart documents={results.documents.map(d => ({ year: d.year, topic: d.topic }))} />
            )}

            {topicViewTab === 'map' && (
              <InteractiveDocumentMap
                documents={results.documents}
                method="umap"
                isBertopic={true}
                topicNames={topicNames}
              />
            )}
          </div>
        )
      })()}

      {results && <AnnotateDatasetPanel />}

      {/* Limit exceeded modal */}
      <Popover open={limitModalOpen} onClose={() => setLimitModalOpen(false)} width={400}>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">AI Limit Reached</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">{limitMessage}</p>
          <p className="text-sm text-gray-500 mb-4">
            You can still view your existing analyses and chat history, but you cannot start new AI jobs until your limit resets.
          </p>
          <button
            onClick={() => setLimitModalOpen(false)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Got it
          </button>
        </div>
      </Popover>
    </div>
  )
}
